// A new organisation: its name, plan (trial), time zone and language. The creator becomes its Owner. When sign-up
// is closed (production), the operating team creates organisations: the page says so instead.

import type { Organisation } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useUser } from '../components/Shell.tsx';
import { Alert, Button, Card, Field, Input, PageHeader, Select, Spinner } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { api, ApiError } from '../lib/api.ts';
import { browserTimeZone } from '../lib/format.ts';
import { keys, useAuthOptions, usePlans } from '../lib/queries.ts';
import { CONTACT_URL } from './auth/AuthPages.tsx';
import { TIME_ZONES } from '../lib/zones.ts';

export function Onboarding() {
  const options = useAuthOptions();
  if (options.isPending) return <div className="flex justify-center py-10"><Spinner /></div>;
  return options.data?.self_sign_up === true ? <CreateOrganisation /> : <AwaitingOrganisation />;
}

/** Sign-up closed: an account without organisation waits for an invitation from the operating team. */
function AwaitingOrganisation() {
  const { t } = useI18n();
  const me = useUser();
  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title={t('onboarding.closedTitle')} />
      <Card>
        <div className="flex flex-col gap-3 text-sm text-slate-700">
          <p>{t('onboarding.closedBody')}</p>
          <p>{t('onboarding.closedInvitation', { email: me.email })}</p>
          <p>
            <a href={CONTACT_URL} className="font-medium text-brand-700 hover:underline">{t('onboarding.closedContact')}</a>
          </p>
        </div>
      </Card>
    </div>
  );
}

function CreateOrganisation() {
  const { t, lang } = useI18n();
  const me = useUser();
  const plans = usePlans();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('');
  const [tz, setTz] = useState(browserTimeZone());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const planId = plan || plans.data?.data[0]?.id || '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const org = await api<Organisation>('/orgs', { method: 'POST', idempotent: true,
        body: { name, plan_id: planId, default_timezone: tz, default_locale: lang } });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate(`/o/${org.id}/settings/sites`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'email_not_verified' ? t('onboarding.verifyFirst') : String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const trialDays = plans.data?.data.find((pl) => pl.id === planId)?.trial_days ?? 0;

  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title={t('onboarding.title')} subtitle={t('onboarding.subtitle')} />
      <Card>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {!me.email_verified && <Alert tone="amber">{t('onboarding.verifyFirst')}</Alert>}
          {error && <Alert tone="red">{error}</Alert>}
          <Field label={t('onboarding.name')} hint={t('onboarding.nameHint')}>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoFocus />}
          </Field>
          <Field label={t('onboarding.plan')} hint={trialDays ? t('onboarding.trial', { days: trialDays }) : undefined}>
            {(p) => (
              <Select {...p} value={planId} onChange={(e) => setPlan(e.target.value)} required>
                {plans.data?.data.map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.name[lang]} — {t('onboarding.planLimits', { devices: pl.limits.devices, users: pl.limits.users })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('common.timezone')}>
            {(p) => (
              <Select {...p} value={tz} onChange={(e) => setTz(e.target.value)}>
                {[...new Set([tz, ...TIME_ZONES])].map((z) => <option key={z} value={z}>{z}</option>)}
              </Select>
            )}
          </Field>
          <Button type="submit" loading={busy} disabled={!me.email_verified || !planId}>{t('onboarding.create')}</Button>
        </form>
      </Card>
    </div>
  );
}
